import { StaggeredAnimation } from "#components/animation/StaggeredAnimation.tsx";
import { StaggeredAnimationItem } from "#components/animation/StaggeredAnimationItem.tsx";
import { ConnectorChannelIcon } from "#components/ui/ConnectorChannelIcon.tsx";
import { useConnectIntegration } from "#features/conversations/hooks/useConnectIntegration.ts";
import { serverRowKey } from "#features/conversations/lib/connectionRows.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import type { McpConnectionPrompt } from "#types/types.ts";
import { Button, Div, Flex } from "@hopper-ui/components";
import { CheckmarkIcon } from "@hopper-ui/icons";
import { useCallback, useMemo, useState } from "react";
import "./AssistantMessageConnectCard.css";

const ICON_SIZE = "1.25rem";

// An org supplies its server's logo URL, so only fetch over http(s) — anything else (data:, blob:,
// javascript:) falls back to the neutral glyph rather than reaching the DOM.
const isFetchableLogoUrl = (logoUrl: string) => {
    try {
        return ["http:", "https:"].includes(new URL(logoUrl).protocol);
    } catch {
        return false;
    }
};

interface ServerIconProps {
    logoUrl?: string | null;
}

const ServerIcon = ({ logoUrl }: ServerIconProps) => {
    const [hasFailed, setHasFailed] = useState(false);
    const { tShared } = useSharedTranslation();

    if (!logoUrl || hasFailed || !isFetchableLogoUrl(logoUrl)) {
        return (
            <ConnectorChannelIcon
                type="None"
                UNSAFE_width={ICON_SIZE}
                UNSAFE_height={ICON_SIZE}
            />
        );
    }

    return (
        <img
            src={logoUrl}
            alt={tShared("channels.unknown")}
            referrerPolicy="no-referrer"
            style={{ width: ICON_SIZE, height: ICON_SIZE, objectFit: "contain" }}
            onError={() => setHasFailed(true)}
        />
    );
};

interface Props {
    servers: McpConnectionPrompt[];
    // The servers the user has connected, so a card can say so.
    connectedServerIds?: ReadonlySet<string>;
    // Set while the request's pause is open: a row can then be dismissed, and a dismissed one says so.
    dismissedKeys?: ReadonlySet<string>;
    onDismiss?: (rowKey: string) => void;
}

export const AssistantMessageConnectServer = ({ servers, connectedServerIds, dismissedKeys, onDismiss }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const connect = useConnectIntegration();

    const handleConnect = useCallback((definitionId: string, name: string) => {
        // A custom server's name and logo URL are org-supplied, so neither goes into tracking.
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Server Card",
            Trigger: "Connect",
            Provider: "CustomRemoteMCP"
        });

        connect({ type: "customServer", definitionId, name });
    }, [trackEvent, connect]);

    const handleDismiss = useCallback((definitionId: string) => {
        trackEvent(MixpanelEvent.ButtonClicked, {
            Location: "Connect Server Card",
            Trigger: "Dismiss",
            Provider: "CustomRemoteMCP"
        });

        onDismiss?.(serverRowKey(definitionId));
    }, [trackEvent, onDismiss]);

    const uniqueServers = useMemo(
        () => {
            const seenDefinitionIds = new Set<string>();

            return servers.filter(({ definitionId }) => {
                if (seenDefinitionIds.has(definitionId)) {
                    return false;
                }

                seenDefinitionIds.add(definitionId);

                return true;
            });
        },
        [servers]
    );

    if (uniqueServers.length === 0) {
        return null;
    }

    return (
        <StaggeredAnimation className="wai-connect-cards" display="flex" flexDirection="column" gap="inline-xs">
            {uniqueServers.map(({ definitionId, name, logoUrl }) => {
                const isConnected = connectedServerIds?.has(definitionId) ?? false;
                const isDismissed = !isConnected && (dismissedKeys?.has(serverRowKey(definitionId)) ?? false);

                return (
                    <StaggeredAnimationItem key={definitionId}>
                        <Flex
                            className="wai-connect-card"
                            gap="inline-sm"
                            paddingX="inset-md"
                            paddingY="inset-sm"
                            borderRadius="rounded-lg"
                            backgroundColor="neutral-weak"
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                shrink={0}
                                backgroundColor="core_samoyed"
                                borderRadius="rounded-md"
                                UNSAFE_width="2rem"
                                UNSAFE_height="2rem"
                            >
                                <ServerIcon logoUrl={logoUrl} />
                            </Flex>
                            <Div className="wai-connect-card-body" data-testid="connect-card-body">
                                <Div fontSize="body-sm" fontWeight="body-md-bold" minWidth={0}>
                                    {name}
                                </Div>
                                {isConnected ? (
                                    <Flex alignItems="center" gap="inline-xs" shrink={0}>
                                        <CheckmarkIcon size="sm" />
                                        <Div fontSize="body-xs-semibold">
                                            {tShared("connectIntegration.connected")}
                                        </Div>
                                    </Flex>
                                ) : isDismissed ? (
                                    <Div fontSize="body-xs-semibold" flexShrink={0}>
                                        {tShared("connectIntegration.dismissed")}
                                    </Div>
                                ) : (
                                    <Flex alignItems="center" wrap="wrap" gap="inline-sm" shrink={0}>
                                        <Button
                                            variant="primary"
                                            size="xs"
                                            flexShrink={0}
                                            onPress={() => handleConnect(definitionId, name)}
                                        >
                                            {tShared("connectIntegration.connect")}
                                        </Button>
                                        {onDismiss && (
                                            <Button
                                                variant="ghost-secondary"
                                                size="xs"
                                                flexShrink={0}
                                                onPress={() => handleDismiss(definitionId)}
                                            >
                                                {tShared("connectIntegration.dismiss")}
                                            </Button>
                                        )}
                                    </Flex>
                                )}
                            </Div>
                        </Flex>
                    </StaggeredAnimationItem>
                );
            })}
        </StaggeredAnimation>
    );
};
